/**
 * Checkout - Fun, exciting order completion flow
 * Features:
 * - Large product previews showing pet portraits
 * - Playful UI that makes buying exciting
 * - Shipping address form
 * - Order summary with celebration vibes
 */
import React, { useState, useEffect } from 'react';
import { COLORS, FONT_FAMILY, SHOP_SPACING, SHOP_BUTTONS } from '../ShopStyles';
import { useCart } from '../context/CartContext';

const Checkout = ({ onNavigate, onComplete }) => {
  const { items, subtotal, shipping, total, clearCart } = useCart();
  const [step, setStep] = useState('shipping');
  
  // Inject animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'checkout-animations';
    styleSheet.textContent = `
      @keyframes bounceIn {
        0% { transform: scale(0.9); opacity: 0; }
        50% { transform: scale(1.02); }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
      @keyframes sparkle {
        0%, 100% { opacity: 1; transform: scale(1) rotate(0deg); }
        50% { opacity: 0.7; transform: scale(1.3) rotate(10deg); }
      }
      @keyframes confetti {
        0% { transform: translateY(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100px) rotate(720deg); opacity: 0; }
      }
      .checkout-product { animation: bounceIn 0.4s ease-out; }
      .celebration-emoji { animation: float 2s ease-in-out infinite; }
      .sparkle { animation: sparkle 1.5s ease-in-out infinite; }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      const existing = document.getElementById('checkout-animations');
      if (existing) existing.remove();
    };
  }, []);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phone: '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinueToPayment = () => {
    if (validateForm()) {
      setStep('payment');
    }
  };

  const handlePlaceOrder = () => {
    setStep('confirmation');
    clearCart();
  };

  // Empty cart
  if (items.length === 0 && step !== 'confirmation') {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>🛒</span>
          <h2 style={styles.emptyTitle}>Your cart is empty!</h2>
          <p style={styles.emptyText}>Let's find some paw-some products first!</p>
          <button onClick={() => onNavigate?.('products')} style={styles.shopButton}>
            🛍️ Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Fun Header */}
      <div style={styles.header}>
        <span className="celebration-emoji" style={styles.headerEmoji}>🎉</span>
        <h1 style={styles.headerTitle}>Almost There!</h1>
        <p style={styles.headerSubtitle}>Your pet's about to become famous 🌟</p>
      </div>

      {/* Progress Steps - Fun version */}
      {step !== 'confirmation' && (
        <div style={styles.progressContainer}>
          <div style={styles.progressSteps}>
            <div style={{
              ...styles.progressStep,
              ...(step === 'shipping' || step === 'payment' ? styles.progressStepActive : {}),
            }}>
              <div style={{
                ...styles.stepCircle,
                background: step === 'shipping' || step === 'payment' 
                  ? 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' 
                  : '#e5e7eb',
              }}>
                {step === 'payment' ? '✓' : '📍'}
              </div>
              <span style={styles.stepLabel}>Shipping</span>
            </div>
            
            <div style={styles.progressLine}>
              <div style={{
                ...styles.progressLineFill,
                width: step === 'payment' ? '100%' : '0%',
              }} />
            </div>
            
            <div style={{
              ...styles.progressStep,
              ...(step === 'payment' ? styles.progressStepActive : {}),
            }}>
              <div style={{
                ...styles.stepCircle,
                background: step === 'payment' 
                  ? 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' 
                  : '#e5e7eb',
              }}>
                💳
              </div>
              <span style={styles.stepLabel}>Payment</span>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Step */}
      {step === 'shipping' && (
        <div style={styles.checkoutLayout}>
          {/* Order Preview - Big and Exciting */}
          <div style={styles.orderPreview}>
            <h2 style={styles.previewTitle}>🎁 Your Order</h2>
            <div style={styles.previewItems}>
              {items.map((item, index) => (
                <div 
                  key={item.cartId} 
                  className="checkout-product"
                  style={{...styles.previewItem, animationDelay: `${index * 0.1}s`}}
                >
                  <div style={styles.previewImageWrapper}>
                    <img src={item.image} alt={item.name} style={styles.previewImage} />
                    {item.petImage && (
                      <div style={styles.previewPetBadge}>
                        <img src={item.petImage} alt={item.petName} style={styles.previewPetImage} />
                      </div>
                    )}
                    {item.quantity > 1 && (
                      <span style={styles.previewQty}>×{item.quantity}</span>
                    )}
                  </div>
                  <div style={styles.previewInfo}>
                    <h3 style={styles.previewName}>{item.name}</h3>
                    <p style={styles.previewVariant}>
                      {item.variant} {item.size && `• ${item.size}`}
                    </p>
                    {item.petName && (
                      <p style={styles.previewPetName}>🐾 Featuring {item.petName}!</p>
                    )}
                    <span style={styles.previewPrice}>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={styles.previewTotals}>
              <div style={styles.totalRow}>
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div style={styles.totalRow}>
                <span>Shipping</span>
                <span style={{color: shipping === 0 ? '#22c55e' : '#374151'}}>
                  {shipping === 0 ? '🎉 FREE' : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              <div style={styles.totalRowFinal}>
                <span>Total</span>
                <span style={styles.totalPrice}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Shipping Form */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>📍 Where should we send this?</h2>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@email.com"
                style={{...styles.input, ...(errors.email ? styles.inputError : {})}}
              />
              {errors.email && <span style={styles.errorText}>{errors.email}</span>}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  style={{...styles.input, ...(errors.firstName ? styles.inputError : {})}}
                />
                {errors.firstName && <span style={styles.errorText}>{errors.firstName}</span>}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  style={{...styles.input, ...(errors.lastName ? styles.inputError : {})}}
                />
                {errors.lastName && <span style={styles.errorText}>{errors.lastName}</span>}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Pet Lover Lane"
                style={{...styles.input, ...(errors.address ? styles.inputError : {})}}
              />
              {errors.address && <span style={styles.errorText}>{errors.address}</span>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Apartment, suite, etc. (optional)</label>
              <input
                type="text"
                name="apartment"
                value={formData.apartment}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div style={styles.formRowThree}>
              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  style={{...styles.input, ...(errors.city ? styles.inputError : {})}}
                />
                {errors.city && <span style={styles.errorText}>{errors.city}</span>}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  style={{...styles.input, ...(errors.state ? styles.inputError : {})}}
                />
                {errors.state && <span style={styles.errorText}>{errors.state}</span>}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>ZIP Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  style={{...styles.input, ...(errors.zipCode ? styles.inputError : {})}}
                />
                {errors.zipCode && <span style={styles.errorText}>{errors.zipCode}</span>}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone (optional)</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="For delivery updates"
                style={styles.input}
              />
            </div>

            <button onClick={handleContinueToPayment} style={styles.continueButton}>
              Continue to Payment 💳
            </button>
            
            <button onClick={() => onNavigate?.('products')} style={styles.backLink}>
              ← Back to Shopping
            </button>
          </div>
        </div>
      )}

      {/* Payment Step */}
      {step === 'payment' && (
        <div style={styles.paymentLayout}>
          <div style={styles.paymentSection}>
            <h2 style={styles.sectionTitle}>💳 Payment</h2>
            
            <div style={styles.paymentBox}>
              <span style={styles.paymentIcon}>🔒</span>
              <h3 style={styles.paymentTitle}>Secure Payment Coming Soon!</h3>
              <p style={styles.paymentText}>
                This is where our Stripe integration will live. 
                For now, click below to complete your demo order!
              </p>
              
              <div style={styles.paymentLogos}>
                <span style={styles.paymentLogo}>💳</span>
                <span style={styles.paymentLogo}>🏦</span>
                <span style={styles.paymentLogo}>📱</span>
              </div>
            </div>

            <div style={styles.buttonRow}>
              <button onClick={() => setStep('shipping')} style={styles.backButton}>
                ← Back
              </button>
              <button onClick={handlePlaceOrder} style={styles.placeOrderButton}>
                🎉 Place Order (Demo)
              </button>
            </div>
          </div>

          {/* Order Summary */}
          <div style={styles.miniSummary}>
            <h3 style={styles.miniTitle}>Order Summary</h3>
            <div style={styles.miniItems}>
              {items.map(item => (
                <div key={item.cartId} style={styles.miniItem}>
                  <img src={item.image} alt={item.name} style={styles.miniImage} />
                  <div style={styles.miniInfo}>
                    <span style={styles.miniName}>{item.name}</span>
                    <span style={styles.miniPrice}>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={styles.miniTotal}>
              <span>Total</span>
              <span style={styles.miniTotalPrice}>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation - Celebration! */}
      {step === 'confirmation' && (
        <div style={styles.confirmationContainer}>
          <div style={styles.confirmationCard}>
            <div style={styles.confettiContainer}>
              <span className="celebration-emoji" style={{...styles.confetti, animationDelay: '0s'}}>🎉</span>
              <span className="celebration-emoji" style={{...styles.confetti, animationDelay: '0.2s'}}>✨</span>
              <span className="celebration-emoji" style={{...styles.confetti, animationDelay: '0.4s'}}>🎊</span>
            </div>
            
            <span style={styles.confirmationIcon}>🎉</span>
            <h1 style={styles.confirmationTitle}>Woohoo! Order Placed!</h1>
            <p style={styles.confirmationText}>
              Your pet is officially on their way to stardom! 
              We'll get started making their amazing products right away.
            </p>
            
            <div style={styles.orderNumber}>
              <span style={styles.orderLabel}>Order Number</span>
              <span style={styles.orderValue}>#PETJR-{Date.now().toString(36).toUpperCase()}</span>
            </div>
            
            <div style={styles.nextSteps}>
              <div style={styles.nextStep}>
                <span>📧</span>
                <span>Confirmation email sent!</span>
              </div>
              <div style={styles.nextStep}>
                <span>🎨</span>
                <span>Production starting soon</span>
              </div>
              <div style={styles.nextStep}>
                <span>📦</span>
                <span>Ships in 6-9 business days</span>
              </div>
            </div>
            
            <div style={styles.confirmationActions}>
              <button onClick={() => onNavigate?.('products')} style={styles.shopMoreButton}>
                🛍️ Shop More Products
              </button>
              <button onClick={() => onNavigate?.('home')} style={styles.homeButton}>
                Back to Home
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: `${SHOP_SPACING.lg} 0`,
    maxWidth: '1100px',
    margin: '0 auto',
  },
  
  // Header
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  headerEmoji: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '12px',
  },
  headerTitle: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '8px',
  },
  headerSubtitle: {
    fontSize: '18px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  
  // Progress
  progressContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '40px',
  },
  progressSteps: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
  },
  progressStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    opacity: 0.5,
  },
  progressStepActive: {
    opacity: 1,
  },
  stepCircle: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#fff',
    fontWeight: '700',
  },
  stepLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  progressLine: {
    width: '100px',
    height: '4px',
    background: '#e5e7eb',
    borderRadius: '2px',
    margin: '0 16px',
    marginBottom: '28px',
    overflow: 'hidden',
  },
  progressLineFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #9333ea 0%, #7c3aed 100%)',
    borderRadius: '2px',
    transition: 'width 0.4s ease',
  },
  
  // Checkout Layout - Products on left, form on right
  checkoutLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    alignItems: 'start',
  },
  
  // Order Preview (Left side)
  orderPreview: {
    background: 'linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%)',
    borderRadius: '24px',
    padding: '28px',
    border: '2px solid #e9d5ff',
  },
  previewTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '20px',
  },
  previewItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  previewItem: {
    display: 'flex',
    gap: '16px',
    background: '#fff',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
  previewImageWrapper: {
    position: 'relative',
    width: '120px',
    height: '120px',
    borderRadius: '12px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  previewPetBadge: {
    position: 'absolute',
    bottom: '6px',
    right: '6px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '3px solid #fff',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  previewPetImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  previewQty: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    background: '#9333ea',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 8px',
    borderRadius: '12px',
  },
  previewInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  previewName: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '4px',
  },
  previewVariant: {
    fontSize: '14px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '6px',
  },
  previewPetName: {
    fontSize: '14px',
    color: '#7c3aed',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '8px',
  },
  previewPrice: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    marginTop: 'auto',
  },
  previewTotals: {
    borderTop: '2px dashed #e5e7eb',
    paddingTop: '16px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '15px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    marginBottom: '8px',
  },
  totalRowFinal: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    paddingTop: '12px',
    borderTop: '2px solid #e5e7eb',
    marginTop: '8px',
  },
  totalPrice: {
    fontSize: '24px',
    color: '#7c3aed',
  },
  
  // Form Section
  formSection: {
    background: '#fff',
    borderRadius: '24px',
    padding: '28px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '16px',
    flex: 1,
  },
  formRow: {
    display: 'flex',
    gap: '16px',
  },
  formRowThree: {
    display: 'flex',
    gap: '12px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: FONT_FAMILY,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  errorText: {
    fontSize: '12px',
    color: '#ef4444',
    fontFamily: FONT_FAMILY,
    marginTop: '4px',
    display: 'block',
  },
  continueButton: {
    width: '100%',
    padding: '18px',
    fontSize: '18px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '14px',
    color: '#fff',
    cursor: 'pointer',
    marginTop: '24px',
    boxShadow: '0 4px 16px rgba(147, 51, 234, 0.3)',
  },
  backLink: {
    width: '100%',
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    padding: '16px',
    marginTop: '8px',
  },
  
  // Payment
  paymentLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 360px',
    gap: '32px',
    alignItems: 'start',
  },
  paymentSection: {
    background: '#fff',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  },
  paymentBox: {
    textAlign: 'center',
    padding: '40px',
    background: 'linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%)',
    borderRadius: '20px',
    marginBottom: '24px',
  },
  paymentIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  paymentTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '12px',
  },
  paymentText: {
    fontSize: '16px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.6,
    margin: 0,
    marginBottom: '20px',
  },
  paymentLogos: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },
  paymentLogo: {
    fontSize: '28px',
  },
  buttonRow: {
    display: 'flex',
    gap: '16px',
  },
  backButton: {
    flex: 1,
    padding: '16px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#f3f4f6',
    border: 'none',
    borderRadius: '12px',
    color: '#6b7280',
    cursor: 'pointer',
  },
  placeOrderButton: {
    flex: 2,
    padding: '16px',
    fontSize: '17px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(34, 197, 94, 0.3)',
  },
  
  // Mini Summary
  miniSummary: {
    background: '#fff',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    position: 'sticky',
    top: '100px',
  },
  miniTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #e5e7eb',
  },
  miniItems: {
    marginBottom: '16px',
  },
  miniItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
  },
  miniImage: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  miniInfo: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
  },
  miniName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  miniPrice: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  miniTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: '12px',
    borderTop: '2px solid #e5e7eb',
    fontSize: '16px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
  },
  miniTotalPrice: {
    color: '#7c3aed',
    fontSize: '18px',
  },
  
  // Confirmation
  confirmationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    padding: '40px 0',
  },
  confirmationCard: {
    background: '#fff',
    borderRadius: '32px',
    padding: '48px',
    textAlign: 'center',
    maxWidth: '500px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
    position: 'relative',
    overflow: 'hidden',
  },
  confettiContainer: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '20px',
  },
  confetti: {
    fontSize: '28px',
  },
  confirmationIcon: {
    fontSize: '80px',
    display: 'block',
    marginBottom: '20px',
  },
  confirmationTitle: {
    fontSize: '32px',
    fontWeight: '800',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '12px',
  },
  confirmationText: {
    fontSize: '17px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.6,
    margin: 0,
    marginBottom: '24px',
  },
  orderNumber: {
    background: 'linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
  },
  orderLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    marginBottom: '4px',
    textTransform: 'uppercase',
  },
  orderValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
  },
  nextSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '28px',
    textAlign: 'left',
  },
  nextStep: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '15px',
    color: '#374151',
    fontFamily: FONT_FAMILY,
  },
  confirmationActions: {
    display: 'flex',
    gap: '12px',
  },
  shopMoreButton: {
    flex: 1,
    padding: '16px',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    cursor: 'pointer',
  },
  homeButton: {
    flex: 1,
    padding: '16px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    background: '#f3f4f6',
    border: 'none',
    borderRadius: '12px',
    color: '#6b7280',
    cursor: 'pointer',
  },
  
  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  emptyIcon: {
    fontSize: '72px',
    display: 'block',
    marginBottom: '20px',
    opacity: 0.6,
  },
  emptyTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '17px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: '28px',
  },
  shopButton: {
    ...SHOP_BUTTONS.primary,
    padding: '16px 32px',
    fontSize: '17px',
  },
};

export default Checkout;
